import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { Fiat, FiatWallet, Provider } from "@models";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TransactionWrapperModalComponent } from "../../../modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { WalletComponent } from "@modules/main/components/wallet.component";
import { FIAT_TYPE } from "@helpers/constants";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { ServiceNames } from "@models/service_enums";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { InitService } from "@core-services/init.service";
import { FiatTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { SegmentService } from "@main-services/segment.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { getTransactionMapKeys } from "@helpers/functions";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FiatWithdrawalModalComponent } from "@modules/main/components/step-summary/fiat-transactions/fiat-withdraw-modal/fiat-withdraw-modal.component";
import { CashPlusRouteName, QueryParamName } from "@modules/cashplus/entities/cashplus.navigation.model";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashplusNavigationService } from "@modules/cashplus/infrastructure/cashplus-navigation.service";
import {
	FiatWalletCashPlusInterface,
	FiatWalletTransactionFacade
} from "@modules/transaction/components/wallet-transaction/fiat-wallet-transaction/fiat-wallet-transaction.facade";
import { combineLatest, Observable } from "rxjs";
import { CashplusCurrentRateInfoModalComponent } from "@modules/cashplus/ui/cashplus-current-rate-info-modal/cashplus-current-rate-info-modal.component";
import { CashplusRollInfoModalComponent } from "@modules/cashplus/ui/cashplus-roll-info-modal/cashplus-roll-info-modal.component";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { CashPlusTrackingFacade } from "@modules/cashplus/+state/cashplus-tracking.facade";
import { take } from "rxjs/operators";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { RudderstackService } from "@main-services/rudderstack.service";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";

@UntilDestroy()
@Component({
	templateUrl: "./fiat-wallet-transaction.component.html",
	styleUrls: ["./fiat-wallet-transaction.component.scss"],
	providers: [CashPlusTrackingFacade],
	standalone: false
})
export class FiatWalletTransactionComponent
	extends WalletComponent<FiatTransaction, Fiat, FiatWallet>
	implements OnInit
{
	private transactionDateFormat: string = "LL";
	private defaultProvider: Provider = null;
	public readonly transactionFilters: string[] = ["buy", "sell", "deposit", "withdrawal"];
	public readonly trackingBasePath: string = "/fiat-wallet";
	public readonly assetType: string = FIAT_TYPE.toLowerCase();
	public getMapKeys = getTransactionMapKeys;
	private cashplusNavigationService = inject(CashplusNavigationService);
	public cashPlusViewModel$: Observable<FiatWalletCashPlusInterface>;
	public portfolioState$ = this.cashplusPortfolioFacade.getPortfolioState$();
	private cashPlusTrackingFacade: CashPlusTrackingFacade = inject(CashPlusTrackingFacade);
	private tinShieldEntryHelper = inject(TinShieldEntryHelper);
	private cdr = inject(ChangeDetectorRef);

	public constructor(
		protected route: ActivatedRoute,
		protected walletService: FiatWalletService,
		protected infoService: InfoService,
		protected tradableListService: TradableListService,
		protected currencyService: FiatService,
		protected fiatService: FiatService,
		protected masterdataService: MasterdataService,
		protected modalService: ModalService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService,
		private router: Router,
		private accountService: AccountService,
		private userService: UserService,
		private translationService: TranslationService,
		private initService: InitService,
		protected activatedRoute: ActivatedRoute,
		protected walletBalancesFacade: WalletsBalancesFacadeService,
		private readonly fiatWalletTransactionFacade: FiatWalletTransactionFacade,
		private readonly cashplusPortfolioFacade: CashplusPortfolioFacade,
		private readonly cashPlusFacade: CashPlusFacade
	) {
		super(activatedRoute);
	}

	public ngOnInit(): void {
		let errorRoute: boolean = false;
		this.route.params.forEach((params: Params) => {
			if (params["pid"]) {
				this.walletId = params["pid"];
			}
			if (!params["pid"] || !this.walletService.getItemById(this.walletId)) {
				errorRoute = true;
				return;
			}

			this.initPagination("fiatwallet/" + this.walletId + "/transactions", FiatTransaction);

			this.paginationComponent
				.getList()
				.pipe(untilDestroyed(this))
				.subscribe((ts: FiatTransaction[]) => {
					ts.forEach((f: FiatTransaction) => f.setAmounts());
					this.cdr.markForCheck();
				});

			if (params["action"] === "success") {
				this.walletService.getTransaction(this.walletId, params["fpid"]).subscribe(
					(t: FiatTransaction) => {
						if (t.getStatus() === "finished") {
							const msg = this.translationService.translate("successful_deposit_for_pid_PID", [
								"F" + params["fpid"]
							]);
							this.infoService.setSuccess(msg, false);

							if (this.paginationComponent.list) {
								const tp = this.paginationComponent.list.find(
									(tran) => tran.getId() === params["fpid"]
								);
								if (tp) {
									if (t.getStatus() !== tp.getStatus()) {
										// Reload the current page instead of exchange the two object because it can break the UI if the applied filter does not match with the new object
										this.paginationComponent.loadPage(this.paginationComponent.pageNumber);
									}
								}
							}
						} else if (t.isConfirmed()) {
							const msg = this.translationService.translate(
								"successful_but_not_processed_deposit_for_pid_PID",
								["F" + params["fpid"]]
							);
							this.infoService.setWarning(msg);
						}
					},
					() =>
						this.infoService.setError(
							this.translationService.translate("cannot_find_deposit_for_pid_PID", ["F" + params["fpid"]])
						)
				);
			}
			if (!params["action"] && params["fpid"]) {
				this.openTransaction(params);
			}
		});
		if (errorRoute) {
			this.infoService.setError(this.translationService.translate("wrong_wallet_id_redirect_error"));
			this.router.navigateByUrl("/portfolio");
			return;
		}
		this.setupWallet();

		this.cashPlusFacade.fetchCombinedCashPlusState();

		this.cashPlusViewModel$ = this.fiatWalletTransactionFacade.fiatWalletViewModel$(
			this.getWallet()?.getCurrencyId()
		);

		this.cashPlusViewModel$.pipe(take(1)).subscribe((vm) => {
			if (vm.isCashPlusEnabled && vm.isFiatEligible) {
				this.cashPlusFacade.fetchFundReleased(this.getCurrency().getPid());
			}
		});

		super.ngOnInit();

		combineLatest({
			balance: this.walletBalancesFacade.getFiatWalletBalance$(this.walletId),
			params: this.route.queryParams
		})
			.pipe(take(1))
			.subscribe(({ balance, params }) => {
				if (params["withdraw"] !== "true") {
					return;
				}
				const minWithdrawal: number = +this.getCurrency().getMinWalletWithdraw();

				if (balance.operationalBalance > minWithdrawal) {
					this.withdraw();
				}

				this.router.navigate([], { queryParams: { ...params, withdraw: null } });
			});
	}

	public setupWallet(): void {
		this.setTransactionDateFormat(this.transactionDateFormat);
		this.walletService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => (this._wallet = this.walletService.getItemById(this.walletId)));
		this.defaultProvider = this.masterdataService.getDefaultPaymentByFiat(this.getWallet().getCurrencyId());
	}

	public openTransaction(params: Params): void {
		this.walletService.getTransaction(this.walletId, params["fpid"]).subscribe((ftx: FiatTransaction) => {
			this.modalService
				.create(TransactionWrapperModalComponent, {
					transaction: ftx,
					canOpen: false
				})
				.present();
			this.router.navigate(["fiat-wallet", params.pid]);
		});
	}

	public withdraw(): void {
		if (!this.hasFunds()) {
			return;
		}
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						return;
					}
					const t = new FiatTransaction();
					t.setFiatWalletId(this.getWallet().getId());
					t.setDirectFiatTransaction(true);
					t["preselected"] = this.getWallet();
					t.setType("withdrawal");
					this.modalService.showModal(FiatWithdrawalModalComponent, { transaction: t });
				}
			});
	}

	public get d_fiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public hasFunds(): boolean {
		if (!this.getWallet()) {
			return false;
		}
		return this.getWallet().getBalance() > 0;
	}

	public fiatName(f: Fiat): string {
		if (!f) {
			return "";
		}
		return f.getMultiLangField("name", this.userService.user.getLanguage());
	}

	public showWalletDetails(): boolean {
		return !!this.getWallet() && this.initService.didInit([ServiceNames.MASTER_DATA]);
	}

	public hasQRCodeDetails(): boolean {
		return !!this.defaultProvider;
	}

	public onClick(): void {
		this.cashPlusTrackingFacade.trackBannerClick("fiat wallet details");
		this.cashplusNavigationService.gotoByNavigationUrl(CashPlusRouteName.Intro);
	}

	public openOptOutModal(): void {
		this.cashplusNavigationService.gotoByNavigationUrl(CashPlusRouteName.OptOut);
	}

	public cashOut(): void {
		this.cashPlusTrackingFacade.trackMoveBalanceToWalletBtnClick("fiat wallet details");
		this.cashplusNavigationService.gotoByNavigationUrl(CashPlusRouteName.CashOut, {
			[QueryParamName.FiatPid]: this.getCurrency().getPid(),
			[QueryParamName.FiatId]: this.getCurrency().getId()
		});
	}

	public openInfoModal(event: string): void {
		if (event === "info_roll") {
			this.modalService.showModal(CashplusRollInfoModalComponent);
		}
		if (event === "current_rate") {
			this.modalService.showModal(CashplusCurrentRateInfoModalComponent);
		}

		if (event === "info_advanced_trading") {
			// TODO: add info modal for advanced trading
			console.warn("info_advanced_trading");
		}
	}
}
